/**
 * @file ConfirmationModal.tsx
 *
 * @description A reusable confirmation modal component for the police officer web application.
 * This component presents a dialog that requires explicit user confirmation before proceeding
 * with potentially important or sensitive actions.
 *
 * @module Modals
 * @requires React
 * @requires Material-UI
 */

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";

/**
 * Props for the ConfirmationModal component
 * @interface ConfirmationModalProps
 * @property {boolean} open - Controls whether the modal is displayed
 * @property {() => void} onClose - Callback function triggered when the modal is dismissed
 * @property {() => void} onConfirm - Callback function triggered when the user confirms the action
 * @property {string} dialogTitle - The title text displayed at the top of the modal
 * @property {string} dialogMessage - The message text displayed in the body of the modal
 */
interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  dialogTitle: string;
  dialogMessage: string;
}

/**
 * @name ConfirmationModal
 *
 * @description A modal dialog that requires user confirmation before proceeding with an action.
 * Used throughout the application when officers need to confirm potentially irreversible actions.
 *
 * The component provides clear visual distinction between confirmation and cancellation options
 * to prevent accidental selections during law enforcement operations.
 *
 * @param {ConfirmationModalProps} props - Component props
 *
 * @returns {React.ReactElement} The rendered confirmation modal
 *
 * @example
 * <ConfirmationModal
 *   open={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   onConfirm={handleConfirmAction}
 *   dialogTitle="Confirm Report Submission"
 *   dialogMessage="Are you sure you want to submit this incident report? It cannot be modified after submission."
 * />
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  dialogTitle,
  dialogMessage,
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      {/* The title section of the modal */}
      <DialogTitle>{dialogTitle}</DialogTitle>

      {/* The content section containing the confirmation message */}
      <DialogContent>
        <Typography>{dialogMessage}</Typography>
      </DialogContent>

      {/* Action buttons for user response */}
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {/* Confirm button - visually distinct primary action button */}
        <Button onClick={onConfirm} variant="contained" color="primary">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationModal;
